/**
 * Futures display math shared by the trade terminal and the web zone (profile,
 * search): price / quantity / settle-coin money formatting per the pair's
 * tick size, lot size and the settle coin's `dps`. Pure — no app imports.
 */

/** The pair fields the settle helpers read (structural — any pair shape fits). */
export interface SettlePair {
  contractType: string;
  lotSize: number;
  baseCurrency: string;
  quoteCurrency: string;
}

/** Placeholder shown in the UI when a value isn't available yet. */
export const EMPTY_VALUE = '—';

/** Price decimals shown when a pair has no usable tick size. */
export const FALLBACK_PRICE_DECIMALS = 2;

/** Quantity decimals shown when a pair has no usable lot size. */
export const FALLBACK_QTY_DECIMALS = 3;

export const INPUT_MAX_DECIMALS = 8;

/**
 * Decimals for a money amount when the coin's own `dps` is unavailable — typed,
 * displayed or compared. Two cases: plain `USD` (the quote of the inverse
 * contracts, where the size is USD contracts) has no `/api/coins` entry at all,
 * and any settle coin before that list resolves. A coin that IS in the list must
 * use its `dps` instead — see `settleDecimals`.
 */
export const MONEY_DECIMALS = 2;

/** Decimals for a percent figure — ROI / ROE / 24h change / margin meters. */
export const PERCENT_DECIMALS = 2;

/** Float-error slack when flooring/ceiling onto a decimal or lot grid
 *  (`0.29 * 100 === 28.999999999999996` must not drop a whole step). */
export const FLOAT_EPSILON = 1e-9;

/** Floor a value to `decimals` places (the 10^decimals grid) — never rounds up
 *  past it, so a "100%" allocation can't exceed the available balance. The
 *  epsilon absorbs binary-float error (`0.29 * 100 === 28.999999999999996`),
 *  which would otherwise drop a value already ON the grid a whole step down;
 *  same guard `floorToLot` uses. */
export function floorTo(value: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.floor(value * f + FLOAT_EPSILON) / f;
}

/** Ceil a value to `decimals` places — the backend's rounding for IM/fee (always
 *  up), so a client preview never undercounts what actually gets charged. */
export function ceilTo(value: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.ceil(value * f) / f;
}

/** Round a non-negative value to `decimals` places, half away from zero (HALF_UP). */
export function roundTo(value: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

/** Grouped number with a fixed number of decimals (en-US grouping). */
export function formatGrouped(value: number, decimals: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * A quote-currency (USD / USDT / USDC) amount: grouped, 2 decimals by default.
 * Use for margin, cost, notional, PnL and balances — money figures that are NOT
 * tied to a pair's tick size.
 */
export function formatUsd(value: number | null | undefined, decimals = MONEY_DECIMALS): string {
  if (value == null) return EMPTY_VALUE;
  return formatGrouped(value, decimals);
}

/**
 * A token (coin) amount — grouped, up to `maxDecimals` significant decimals with
 * trailing zeros trimmed (0.00016234, 1.5). Use for inverse-pair settle figures
 * (margin / PnL / fees / balance in the base coin), which are far smaller than 1
 * and need more precision than the 2-dp {@link formatUsd}.
 */
export function formatTokenAmount(value: number, maxDecimals = INPUT_MAX_DECIMALS): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: maxDecimals });
}

/**
 * A settle-coin money amount (margin / PnL / fees / balance). Once the settle
 * coin's precision is known it PINS the count for both contract types — a figure
 * denominated in a coin reads at exactly that coin's `dps`, so "0.000500 BTC"
 * never collapses to "0.0005" while the linear "12.700 USDT" keeps its zeros.
 * Without `decimals` it falls back to the old defaults: base-coin precision
 * (trailing zeros trimmed) for inverse, USD cents for linear.
 */
export function formatSettleAmount(
  value: number | null | undefined,
  inverse: boolean,
  decimals?: number,
): string {
  if (value == null) return EMPTY_VALUE;
  if (decimals != null) return formatGrouped(value, decimals);
  return inverse ? formatTokenAmount(value) : formatUsd(value);
}

/**
 * Grouped and FLOORED to `decimals` — for a held QUANTITY (§ `formatQty`), whose
 * sizing floors to the lot grid too (`floorToLot`), so the number on screen and
 * what "100%" actually sizes agree.
 *
 * NOT for money. A wallet balance formats like the Cost beside it
 * (`formatSettleAmount`): flooring the balance while the cost rounds makes an
 * order at 100% read as costing MORE than the balance shown. And flooring a
 * signed figure (PnL, equity) pushes a loss further from zero.
 */
export function formatFloored(value: number, decimals: number): string {
  return formatGrouped(floorTo(value, decimals), decimals);
}

/**
 * A settle-coin money amount with an explicit sign — positives get a leading `+`,
 * negatives keep their `-`. Use for signed figures like PnL where the direction
 * should read at a glance (e.g. "+89.39", "-12.50").
 */
export function formatSignedSettleAmount(
  value: number,
  inverse: boolean,
  decimals?: number,
): string {
  const v = snapZero(value, decimals ?? (inverse ? INPUT_MAX_DECIMALS : MONEY_DECIMALS));
  const sign = v > 0 ? '+' : '';
  return `${sign}${formatSettleAmount(v, inverse, decimals)}`;
}

/**
 * Snap a value that rounds to zero at the given precision to a clean +0, so we
 * never render "-0.00" / "-0.000%" for tiny negative results (a sub-precision
 * loss displayed as a signed zero reads as a bug). Pass the decimals the value
 * will be shown at; anything within half a ULP of zero becomes 0.
 */
export function snapZero(value: number, decimals = MONEY_DECIMALS): number {
  if (!Number.isFinite(value)) return 0;
  return Math.abs(value) < 0.5 * 10 ** -decimals ? 0 : value;
}

/**
 * Price decimals from a pair's `tickSize`, per the backend contract:
 * `max(0, -floor(log10(tickSize)))` — 0.5→1, 0.01→2, 0.001→3, 0.0001→4.
 * Falls back to 2 for a missing / non-positive tick.
 */
export function priceDecimals(tickSize: number | null | undefined): number {
  if (tickSize == null || !Number.isFinite(tickSize) || tickSize <= 0) {
    return FALLBACK_PRICE_DECIMALS;
  }
  return Math.max(0, -Math.floor(Math.log10(tickSize)));
}

/** A price, grouped and shown to the pair's tick-size precision. */
export function formatPrice(
  value: number | null | undefined,
  tickSize: number | null | undefined,
): string {
  if (value == null) return EMPTY_VALUE;
  return formatGrouped(value, priceDecimals(tickSize));
}

/**
 * Quantity decimals from a pair's `lotSize`, mirroring the price rule:
 * `max(0, -floor(log10(lotSize)))` — 0.001→3, 0.1→1, 1→0, 10→0.
 * Falls back to 3 for a missing / non-positive lot.
 */
export function quantityDecimals(lotSize: number | null | undefined): number {
  if (lotSize == null || !Number.isFinite(lotSize) || lotSize <= 0) {
    return FALLBACK_QTY_DECIMALS;
  }
  return Math.max(0, -Math.floor(Math.log10(lotSize)));
}

/**
 * A base quantity, grouped and shown to the pair's lot-size precision. Floored
 * (not rounded) to that precision: a held / closeable quantity must never display
 * MORE than the user actually has (e.g. 0.00101183 → 0.001011, not 0.001012).
 */
export function formatQty(
  value: number | null | undefined,
  lotSize: number | null | undefined,
): string {
  if (value == null) return EMPTY_VALUE;
  return formatFloored(value, quantityDecimals(lotSize));
}

/** Decimal places for an order quantity, by contract type. */
export function contractQtyDecimals(inverse: boolean, lotSize: number | null | undefined): number {
  return inverse ? MONEY_DECIMALS : quantityDecimals(lotSize);
}

/** A grouped order quantity for display, by contract type. */
export function formatContractQty(
  value: number | null | undefined,
  inverse: boolean,
  lotSize: number | null | undefined,
): string {
  if (value == null) return EMPTY_VALUE;
  return inverse ? formatUsd(value, MONEY_DECIMALS) : formatQty(value, lotSize);
}

/** True for coin-margined (inverse) pairs. */
export function isInversePair(pair?: { contractType: string } | null): boolean {
  return pair?.contractType === 'INVERSE_PERPETUAL';
}

/** Coin a pair's money figures (margin/PnL/fees/balance) settle in. */
export function settleCurrency(pair?: SettlePair | null): string {
  return isInversePair(pair) ? pair!.baseCurrency : (pair?.quoteCurrency ?? 'USDT');
}

/**
 * Display decimals for a pair's settle-coin money (margin / PnL / base-coin
 * value): the settle coin's `dps` (`decimalPlacesShort`) from the coins list.
 *
 * Exception — inverse pairs settle in the BASE coin (BTC/ETH/SOL), a native token
 * that IS in the pairs list, so its precision comes from the pair's own lot size. Only
 * coins absent from the pairs list (the stables — USDT/USDC/BONUS) read `dps`.
 *
 * Unknown coin (plain `USD`, or the list hasn't loaded yet) → {@link MONEY_DECIMALS},
 * so money doesn't briefly render at the pair's lot-size precision (1234.567890 USDT)
 * and then snap to 1234.57 once the coins arrive.
 */
export function settleDecimals(
  pair: SettlePair | undefined,
  coins: ReadonlyArray<{ shortName: string; decimalPlacesShort: number }>,
): number {
  if (isInversePair(pair)) return quantityDecimals(pair?.lotSize);
  const ticker = settleCurrency(pair).toUpperCase();
  const coin = coins.find((c) => c.shortName.toUpperCase() === ticker);
  return coin?.decimalPlacesShort ?? MONEY_DECIMALS;
}

/** ROE% on the position's own margin (use the NET PnL for the fee-aware ROE). */
export function roePercent(pnl: number, marginCol: number): number {
  return marginCol > 0 ? (pnl / marginCol) * 100 : 0;
}

/**
 * ROE% display string ("+1.23%"). Snaps a sub-precision value to a signless
 * "0.00%" — never "-0.00%".
 */
export function formatRoePercent(roe: number): string {
  const display = Number(roe.toFixed(PERCENT_DECIMALS)) === 0 ? 0 : roe;
  const sign = display > 0 ? '+' : '';
  return `${sign}${display.toFixed(PERCENT_DECIMALS)}%`;
}

/** Position value in the base coin (inverse): `q / price` (BTC). */
export function positionValueBase(quantity: number, price: number): number {
  return price > 0 ? quantity / price : 0;
}

/** Position value before leverage. */
export function notionalValue(entryPrice: number, quantity: number): number {
  return entryPrice * quantity;
}

/**
 * Gross uPnL in the settle coin at `price`. Linear: `(price − entry) × q × dir`;
 * inverse: `q × (1/entry − 1/price) × dir` (base).
 */
export function contractUnrealizedPnl(
  inverse: boolean,
  side: 'long' | 'short',
  entryPrice: number,
  price: number,
  quantity: number,
): number {
  const dir = side === 'long' ? 1 : -1;
  if (!inverse) return (price - entryPrice) * quantity * dir;
  if (entryPrice <= 0 || price <= 0) return 0;
  return quantity * (1 / entryPrice - 1 / price) * dir;
}
